import { Home, BookOpen, UserCircle, Trophy, Crown, Users } from 'lucide-react';
import { motion } from 'framer-motion';

export type TabId = 'home' | 'learn' | 'leaderboard' | 'playbuilder' | 'pricing' | 'profile' | 'players';

interface BottomNavProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  badges?: Partial<Record<TabId, number>>;
  showPlayersTab?: boolean;
  hidePricingTab?: boolean;
}

export function BottomNav({ activeTab, onTabChange, badges = {}, showPlayersTab = false, hidePricingTab = false }: BottomNavProps) {
  const tabs: { id: TabId; label: string; icon: typeof Home }[] = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'learn', label: 'Learn', icon: BookOpen },
    { id: 'leaderboard', label: 'Ranks', icon: Trophy },
    ...(showPlayersTab
      ? [
          { id: 'players' as TabId, label: 'Players', icon: Users },
          { id: 'profile' as TabId, label: 'Profile', icon: UserCircle },
        ]
      : hidePricingTab
      ? [{ id: 'profile' as TabId, label: 'Profile', icon: UserCircle }]
      : [
          { id: 'pricing' as TabId, label: 'Pro', icon: Crown },
          { id: 'profile' as TabId, label: 'Profile', icon: UserCircle },
        ]),
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-background safe-area-bottom"
      style={{ borderTop: '1px solid rgba(255,255,255,0.1)', zIndex: 1000 }}
    >
      <div className="max-w-lg mx-auto flex items-center justify-around h-16">
        {tabs.map(tab => {
          const active = activeTab === tab.id;
          const badge = badges[tab.id];
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors relative"
            >
              {active && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <div className="relative">
                <tab.icon
                  className="w-5 h-5 transition-colors"
                  style={{ color: active ? 'hsl(var(--primary))' : '#6B7280' }}
                />
                {badge != null && badge > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-display font-bold leading-none">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
              <span
                className="text-[10px] font-display font-bold tracking-wide uppercase transition-colors"
                style={{ color: active ? 'hsl(var(--primary))' : '#6B7280' }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
